var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spin, message } from "antd";
import { CheckCircleTwoTone, CloseOutlined, UserAddOutlined } from "@ant-design/icons";
import styled from "styled-components";
import apiClientServices from "../../api-client";
import { MANAGER_ENDPOINTS } from "../../utils/endpoints";
import { useApplicationContext } from "../../context/application-context";
const InviteRoot = styled.div `
  margin: 12px 0 16px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e4e5e6;
  background: #fff;
`;
const InviteHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: #181d27;
  font-size: 13px;
  font-weight: 600;
`;
const InviteHint = styled.div `
  margin-bottom: 10px;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 18px;
`;
const SearchWrap = styled.div `
  position: relative;
  width: 100%;
`;
const InputContainer = styled.div `
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 42px;
  padding: 6px 10px;
  border: 1px solid #d5d7da;
  border-radius: 6px;
  background: #fff;

  &:focus-within {
    border-color: ${(props) => { var _a, _b; return ((_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colorPalette) === null || _b === void 0 ? void 0 : _b.primaryColor) || "#5b36f0"; }};
  }
`;
const ManagerTag = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(props) => { var _a, _b; return `${((_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colorPalette) === null || _b === void 0 ? void 0 : _b.primaryColor) || "#5b36f0"}18`; }};
  color: ${(props) => { var _a, _b; return ((_b = (_a = props.theme) === null || _a === void 0 ? void 0 : _a.colorPalette) === null || _b === void 0 ? void 0 : _b.primaryColor) || "#5b36f0"; }};
  font-size: 12px;
  font-weight: 500;
`;
const TagClose = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 10px;
`;
const SearchInput = styled.input `
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13px;
  color: #181d27;

  &::placeholder {
    color: #a0a4b8;
  }
`;
const SearchDropdown = styled.div `
  position: absolute;
  z-index: 20;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
`;
const DropdownItem = styled.button `
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  background: ${(props) => (props.$selected ? "#fff5f5" : "#fff")};
  text-align: left;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.45 : 1)};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) => props.$disabled ? "#fff" : props.$selected ? "#fff5f5" : "#fafafa"};
  }

  .item-name {
    color: #181d27;
    font-size: 13px;
    font-weight: 500;
  }

  .item-email {
    color: #8c8c8c;
    font-size: 12px;
  }
`;
const ActionsRow = styled.div `
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;
const InviteButton = styled(Button) `
  && {
    height: 34px;
    border-radius: 6px;
    font-weight: 500;
  }
`;
/**
 * In-meeting invite strip for Primary HM.
 * Search: GET /hiring-manager/search-hm. Invite: POST /invite-additional-hms.
 */
const InviteAdditionalHm = ({ candidateUserId, courseId, stageId, panelInterviewId, }) => {
    const { user } = useApplicationContext();
    const [selected, setSelected] = useState([]);
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [searching, setSearching] = useState(false);
    const [inviting, setInviting] = useState(false);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const selfId = String((user === null || user === void 0 ? void 0 : user.id) || sessionStorage.getItem("userId") || "");
    const selfEmail = ((user === null || user === void 0 ? void 0 : user.email) || sessionStorage.getItem("email") || "")
        .trim()
        .toLowerCase();
    const selectedIds = useMemo(() => new Set(selected.map((item) => String(item.id))), [selected]);
    const isSelf = (manager) => {
        var _a;
        if (selfId && String(manager.id) === selfId) {
            return true;
        }
        if (selfEmail && ((_a = manager.email) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase()) === selfEmail) {
            return true;
        }
        return false;
    };
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);
    const searchManagers = (value) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        try {
            const response = yield apiClientServices.get(`${MANAGER_ENDPOINTS.SEARCH_MANAGER_BY_NAME_EMAIL}?searchTerm=${encodeURIComponent(value)}`);
            if (((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.status) === 200) {
                const list = (((_b = response === null || response === void 0 ? void 0 : response.data) === null || _b === void 0 ? void 0 : _b.result) || [])
                    .map((item) => ({
                    id: String(item.id),
                    name: item.name || "",
                    email: item.email || "",
                }))
                    .filter((item) => item.id && item.email);
                setResults(list);
                setIsOpen(true);
            }
            else {
                setResults([]);
            }
        }
        catch (_c) {
            message.error("Failed to search managers");
            setResults([]);
        }
        finally {
            setSearching(false);
        }
    });
    const handleSearchChange = (event) => {
        const value = event.target.value;
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        if (!value || value.trim() === "") {
            setResults([]);
            setSearching(false);
            setIsOpen(false);
            return;
        }
        setSearching(true);
        searchTimeoutRef.current = setTimeout(() => {
            searchManagers(value.trim());
        }, 400);
    };
    const handleSelect = (manager) => {
        if (isSelf(manager)) {
            message.warning("You cannot invite yourself as an Additional HM");
            return;
        }
        if (selectedIds.has(String(manager.id))) {
            setSelected((prev) => prev.filter((item) => String(item.id) !== String(manager.id)));
        }
        else {
            if (selected.length >= 5) {
                message.warning("You can select up to 5 Additional HMs");
                return;
            }
            setSelected((prev) => [...prev, manager]);
        }
        setIsOpen(false);
        setResults([]);
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };
    const handleRemove = (id) => {
        setSelected((prev) => prev.filter((item) => String(item.id) !== String(id)));
    };
    const handleInvite = () => __awaiter(void 0, void 0, void 0, function* () {
        var _d, _e;
        if (!selected.length) {
            message.warning("Select at least one manager to invite");
            return;
        }
        if (!candidateUserId || !courseId) {
            message.error("Missing candidate or course context");
            return;
        }
        if (!selfId) {
            message.error("Unable to identify the primary hiring manager");
            return;
        }
        setInviting(true);
        try {
            const response = yield apiClientServices.post(MANAGER_ENDPOINTS.INVITE_ADDITIONAL_HMS, Object.assign(Object.assign(Object.assign({ userId: Number(candidateUserId), courseId: Number(courseId), primaryHmId: Number(selfId), additionalHmIds: selected.map((item) => item.id), additionalHmEmails: selected.map((item) => item.email), additionalHmNames: selected.map((item) => item.name) }, (stageId != null ? { stageId: Number(stageId) } : {})), (panelInterviewId != null
                ? { panelInterviewId: Number(panelInterviewId) }
                : {})), { inviteSource: "IN_MEETING" }));
            if (((_d = response === null || response === void 0 ? void 0 : response.data) === null || _d === void 0 ? void 0 : _d.status) === 200) {
                message.success(`Invited ${selected.length} Additional HM${selected.length > 1 ? "s" : ""}`);
                setSelected([]);
                return;
            }
            message.error(((_e = response === null || response === void 0 ? void 0 : response.data) === null || _e === void 0 ? void 0 : _e.message) || "Failed to invite Additional HMs");
        }
        catch (_f) {
            message.error("Failed to invite Additional HMs");
        }
        finally {
            setInviting(false);
        }
    });
    return (_jsxs(InviteRoot, { children: [_jsxs(InviteHeader, { children: [_jsx(UserAddOutlined, {}), "Invite Additional HM"] }), _jsx(InviteHint, { children: "Search hiring managers to invite them to this meeting." }), _jsxs(SearchWrap, { children: [_jsxs(InputContainer, { children: [selected.map((manager) => (_jsxs(ManagerTag, Object.assign({ title: manager.email }, { children: [_jsx("span", { children: manager.name }), _jsx(TagClose, Object.assign({ type: "button", "aria-label": `Remove ${manager.name}`, onClick: () => handleRemove(manager.id) }, { children: _jsx(CloseOutlined, {}) }))] }), manager.id))), _jsx(SearchInput, { ref: inputRef, type: "text", placeholder: "Search by name or email", onChange: handleSearchChange, onFocus: () => {
                                    if (results.length > 0) {
                                        setIsOpen(true);
                                    }
                                } })] }), isOpen && (searching || results.length > 0) && (_jsx(SearchDropdown, Object.assign({ ref: dropdownRef }, { children: searching ? (_jsx("div", Object.assign({ style: { padding: 16, textAlign: "center" } }, { children: _jsx(Spin, { size: "small" }) }))) : (results.map((item) => {
                            const self = isSelf(item);
                            const already = selectedIds.has(String(item.id));
                            return (_jsxs(DropdownItem, Object.assign({ type: "button", "$selected": already, "$disabled": self, onClick: () => handleSelect(item) }, { children: [_jsxs("div", { children: [_jsxs("div", Object.assign({ className: "item-name" }, { children: [item.name, self ? " (You)" : ""] })), _jsx("div", Object.assign({ className: "item-email" }, { children: item.email }))] }), already && _jsx(CheckCircleTwoTone, {})] }), item.id));
                        })) })))] }), _jsx(ActionsRow, { children: _jsxs(InviteButton, Object.assign({ type: "primary", loading: inviting, disabled: !selected.length || inviting, onClick: handleInvite }, { children: ["Invite", selected.length ? ` (${selected.length})` : ""] })) })] }));
};
export default InviteAdditionalHm;
